import { AuthenticatedHttpService } from 'http/httpService'
import { t } from 'translations'

export type SalesChannelType = 'pos'

export interface PosPaymentMethod {
    id: number
    name: string
    salesChannelType: SalesChannelType
    supportsPartialPayment: boolean
    sortOrder: number
    isEnabled: boolean
    isArchived: boolean
}

export interface NewPosPaymentMethod {
    name: string
    salesChannelType: SalesChannelType
    supportsPartialPayment: boolean
    sortOrder: number
}

export interface BulkUpdateItem {
    id: number
    isEnabled: boolean
    isArchived: boolean
    sortOrder: number
}

const parseErrorResponse = async (response: Response, fallback: string): Promise<string> => {
    try {
        const body = await response.json()
        const messages = Object.values(body).flat()
        if (messages.length > 0) {
            return messages.join('. ')
        }
    } catch {
        // response body is not JSON
    }
    return fallback
}

export class PosPaymentMethodsService {
    constructor(private httpService: AuthenticatedHttpService, private backofficeEndpoint: string) {}

    private baseUrl(accountSlug: string) {
        return `${this.backofficeEndpoint}dashboard_api/accounts/${accountSlug}/custom_payment_methods/`
    }

    async getPaymentMethods(accountSlug: string): Promise<PosPaymentMethod[]> {
        const response = await this.httpService.fetch(this.baseUrl(accountSlug))
        if (!response.ok) {
            throw new Error(
                await parseErrorResponse(
                    response,
                    t('settings.accountSettings.paymentMethods.pos.errors.unableToRetrieve'),
                ),
            )
        }
        return await response.json()
    }

    async createPaymentMethod(accountSlug: string, data: NewPosPaymentMethod): Promise<PosPaymentMethod> {
        const response = await this.httpService.fetch(this.baseUrl(accountSlug), {
            method: 'POST',
            body: JSON.stringify(data),
            headers: { 'Content-Type': 'application/json' },
        })
        if (!response.ok) {
            throw new Error(
                await parseErrorResponse(
                    response,
                    t('settings.accountSettings.paymentMethods.pos.errors.unableToCreate'),
                ),
            )
        }
        return await response.json()
    }

    async updatePaymentMethod(
        accountSlug: string,
        id: number,
        data: Partial<PosPaymentMethod>,
    ): Promise<PosPaymentMethod> {
        const response = await this.httpService.fetch(`${this.baseUrl(accountSlug)}${id}/`, {
            method: 'PATCH',
            body: JSON.stringify(data),
            headers: { 'Content-Type': 'application/json' },
        })
        if (!response.ok) {
            throw new Error(
                await parseErrorResponse(
                    response,
                    t('settings.accountSettings.paymentMethods.pos.errors.unableToUpdate'),
                ),
            )
        }
        return await response.json()
    }

    async deletePaymentMethod(accountSlug: string, id: number): Promise<void> {
        const response = await this.httpService.fetch(`${this.baseUrl(accountSlug)}${id}/`, {
            method: 'DELETE',
        })
        if (!response.ok) {
            throw new Error(
                await parseErrorResponse(
                    response,
                    t('settings.accountSettings.paymentMethods.pos.errors.unableToDelete'),
                ),
            )
        }
    }

    async bulkUpdate(accountSlug: string, items: BulkUpdateItem[]): Promise<void> {
        const response = await this.httpService.fetch(`${this.baseUrl(accountSlug)}bulk/`, {
            method: 'POST',
            body: JSON.stringify(items),
            headers: { 'Content-Type': 'application/json' },
        })
        if (!response.ok) {
            throw new Error(
                await parseErrorResponse(
                    response,
                    t('settings.accountSettings.paymentMethods.pos.errors.unableToBulkUpdate'),
                ),
            )
        }
    }
}
